import React from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import { EventRepeat } from "@mui/icons-material";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import { Transaction } from "../utils/types";
import { translate } from "../utils/currency";
import { getStatusMeta, formatDateTime, operationLabels } from "../utils/utils";

/** One label/value pair of the detail grid. */
const Field: React.FC<{
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  wide?: boolean;
  onCopy?: () => void;
}> = ({ label, value, mono, wide, onCopy }) => (
  <Box sx={{ minWidth: 0, gridColumn: wide ? "1 / -1" : undefined }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        display: "block",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: ".05em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{
          fontSize: 13,
          fontWeight: 560,
          fontFamily: mono ? "monospace" : undefined,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value || "—"}
      </Typography>
      {onCopy && (
        <IconButton
          aria-label={`Copy ${label.toLowerCase()}`}
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onCopy();
          }}
          // The card is phone-only, so the target must be finger-sized.
          sx={{ width: 34, height: 34, flexShrink: 0 }}
        >
          <ContentCopyRounded sx={{ fontSize: 15 }} />
        </IconButton>
      )}
    </Box>
  </Box>
);

export const TransactionCard: React.FC<{
  t: Transaction;
}> = ({ t }) => {
  const theme = useTheme();
  const copy = (value: string) => navigator.clipboard.writeText(value);

  const status = getStatusMeta(t.status, theme.palette.mode);
  const created = formatDateTime(t.created_at);
  const updated = t.updated_at ? formatDateTime(t.updated_at) : null;

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: "16px",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={1}
      >
        <Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1,
              py: 0.4,
              mb: 0.75,
              borderRadius: "8px",
              backgroundColor: alpha(status.color, 0.12),
              border: `1px solid ${alpha(status.color, 0.28)}`,
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: status.color,
              }}
            />
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: status.textColor }}
            >
              {status.label}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {operationLabels[t.transaction_type] || t.transaction_type}
          </Typography>
        </Box>

        <Box textAlign="right" sx={{ flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 740,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {t.start_amount.toLocaleString("en-US")}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 0.5, fontWeight: 700 }}
            >
              {t.currency}
            </Typography>
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              color: t.paid_amount
                ? theme.palette.semanticText.success
                : "text.secondary",
            }}
          >
            confirmed {t.paid_amount?.toLocaleString("en-US") || "0"}
          </Typography>
        </Box>
      </Box>

      <Divider />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          rowGap: 1.25,
          columnGap: 1.5,
        }}
      >
        <Field
          label="Transaction ID"
          value={t.id}
          mono
          wide
          onCopy={() => copy(t.id)}
        />
        <Field
          label="Order ID"
          value={t.merchant_order_id}
          mono
          wide
          onCopy={() => copy(t.merchant_order_id || "")}
        />
        <Field
          label="Requisites"
          value={
            t.payment_method === "bol_qr_code" ? t.qr_code : t.number
          }
          mono
        />
        <Field label="Full name" value={t.fullname} />
        <Field label="Method" value={translate(t.payment_method)} />
        <Field label="Bank" value={t.bank_name} />
        <Field label="User" value={t.user_id} mono />
        <Field
          label="Rate / fee"
          value={`${t.our_rate}% / ${t.our_fee?.toLocaleString("en-US") ?? 0}`}
        />
      </Box>

      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="caption" color="text.secondary">
          {created.date} {created.time}
        </Typography>
        {updated && (
          <Tooltip title={`Updated: ${updated.date} ${updated.time}`}>
            <EventRepeat sx={{ fontSize: 14, color: "text.secondary" }} />
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
};
